import React, {Component, ReactElement} from "react";
import {ApplicationError} from "../../../common/errors";
import ErrorPanel from "../error";
import Loader from "../loader";
import NamedSelect, {NamedItem} from "./select-named";

export interface DynamicSelectProps<T> {
  by?: string;
  load: () => Promise<T[]>;
  onSelect: (item: T | null) => void;
  disallowEmpty?: boolean;
  onLoaded?: (items: T[]) => void;
  initialValue?: string | T;
  disabled?: boolean;
  allowEmptyForced?: boolean;
}

interface DynamicSelectState<T> {
  loading: boolean;
  error?: ApplicationError;
  items: T[];
  selectedItemId: string;
}

/**
 * Generic component to display select controls where options are fetched
 * dynamically.
 */
export default class DynamicSelect<T extends NamedItem> extends Component<
  DynamicSelectProps<T>,
  DynamicSelectState<T>
> {
  constructor(props: DynamicSelectProps<T>) {
    super(props);

    this.state = {
      loading: true,
      items: [],
      selectedItemId: "",
    };
  }

  setInitialItem(items: T[]): T | undefined {
    if (items.length === 0) {
      return undefined;
    }

    // if the options contain a single element, select it automatically
    if (items.length === 1 && this.props.allowEmptyForced !== true) {
      const firstItem = items[0];

      this.props.onSelect(firstItem);
      return firstItem;
    }

    const {initialValue} = this.props;
    if (!initialValue) {
      if (this.props.disallowEmpty) {
        const firstItem = items[0];

        this.props.onSelect(firstItem);
        return firstItem;
      }
      return;
    }

    const item = items.find(
      (item) =>
        item === initialValue ||
        item.id === initialValue ||
        item.name === initialValue
    );

    this.props.onSelect(item || null);
    return item;
  }

  load(): void {
    this.setState({
      loading: true,
      error: undefined,
    });
    this.props.load().then(
      (items) => {
        if (this.props.onLoaded) {
          this.props.onLoaded(items);
        }

        const initialItem = this.setInitialItem(items);

        this.setState({
          items,
          loading: false,
          selectedItemId: initialItem ? initialItem.id : "",
        });
      },
      (error: ApplicationError) => {
        this.setState({
          loading: false,
          error,
        });
      }
    );
  }

  componentDidUpdate(props: DynamicSelectProps<T>): void {
    if (props.by !== this.props.by) {
      this.load();
    }
  }

  componentDidMount(): void {
    this.load();
  }

  renderOptions(): ReactElement {
    const {allowEmptyForced, disabled, disallowEmpty} = this.props;
    const {error, loading, items, selectedItemId} = this.state;

    if (error) {
      return <ErrorPanel error={error} />;
    }

    if (loading) {
      return <Loader className="mini" />;
    }

    return (
      <NamedSelect
        items={items}
        onSelect={this.props.onSelect}
        initialValue={selectedItemId}
        disallowEmpty={disallowEmpty}
        allowEmptyForced={allowEmptyForced}
        disabled={disabled}
      />
    );
  }

  render(): ReactElement {
    return (
      <React.Fragment>
        {this.renderOptions()}
        {this.props.children}
      </React.Fragment>
    );
  }
}
